import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { DocVersionDetail, VersionRestoreReport } from "../api";
import { ApiError } from "../api/types";
import { BookOpen, Clock, Plus, RotateCcw, X } from "../compose/Icon";
import { EmptyState, ErrorState, Spinner } from "../compose/states";
import { useToast } from "../compose/Toast";
import { actorFromLegacy } from "../lib/avatar";
import { formatDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { VersionCanvas } from "./canvas/VersionCanvas";
import { Avatar } from "./shared/Avatar";
import { RestoreConfirm, RestoreReportCard } from "./VersionRestore";

/**
 * Version history panel on the document viewer. Lists the document's recorded
 * versions (newest first) with the author + timestamp + note captured at
 * record time, lets a reviewer snapshot the current content ("Save version"),
 * and opens any past version in a read-only overlay.
 *
 * The put-it-back action is called REVERT here and on the workbench timeline,
 * and the two share `<RestoreConfirm>`. One operation, one name: the module and
 * the wire keep the older `restore` spelling (`restoreVersion`,
 * `restored_from_version_id`, `VersionRestoreReport`) because renaming a typed
 * column and a shipped terminal to match a label is a migration in exchange for
 * nothing — but a user must never meet both words for the same button. All membership-scoped by
 * the backend; a non-member never reaches this document.
 */
export function VersionHistoryPanel({ docId }: { docId: string }) {
  const toast = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const { data: versions, loading, error } = useAsync(
    () => api.listVersions(docId),
    [docId, reloadKey],
  );
  const [viewing, setViewing] = useState<DocVersionDetail | null>(null);
  const [saving, setSaving] = useState(false);
  /** The version a human has asked to restore, awaiting confirmation. */
  const [confirming, setConfirming] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);
  /** The server's account of what the last restore MOVED. */
  const [report, setReport] = useState<VersionRestoreReport | null>(null);

  const openVersion = useCallback(
    async (id: number) => {
      try {
        setViewing(await api.getVersion(docId, id));
      } catch {
        toast.show("Couldn't open that version");
      }
    },
    [docId, toast],
  );

  const saveSnapshot = useCallback(async () => {
    setSaving(true);
    try {
      await api.createVersion(docId);
      setReloadKey((k) => k + 1);
      toast.show("Version saved");
    } catch {
      toast.show("Couldn't save a version");
    } finally {
      setSaving(false);
    }
  }, [docId, toast]);

  const restore = useCallback(
    async (versionId: number) => {
      setRestoring(true);
      try {
        const result = await api.restoreVersion(docId, versionId);
        setReport(result);
        setConfirming(null);
        setReloadKey((k) => k + 1);
        // Never say "restored" when the server says nothing moved — a silent
        // no-op reported as success is the exact failure this feature exists
        // to make impossible.
        toast.show(
          result.bytes_written
            ? "Reverted — saved as a new version"
            : "Nothing changed — the stored content already matched that version",
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          toast.show("You don't have permission to revert versions");
        } else if (err instanceof ApiError && err.status === 404) {
          toast.show("That version is no longer available");
        } else if (err instanceof ApiError && err.status === 409) {
          // The server REFUSED with a reason (an empty source version would
          // wipe a document that has a body). Show its words, not ours — a
          // generic "couldn't restore" is how an operator retries the exact
          // write that was just refused for a good reason.
          toast.show(err.message);
        } else {
          toast.show("Couldn't revert to that version");
        }
      } finally {
        setRestoring(false);
      }
    },
    [docId, toast],
  );

  return (
    <div className="rail__card" data-testid="version-history">
      <h3 className="rail__h">
        History
        {versions && versions.length > 0 ? (
          <span className="rail__count">{versions.length}</span>
        ) : null}
      </h3>

      <button
        type="button"
        className="btn btn--ghost btn--sm"
        data-testid="version-save"
        disabled={saving}
        onClick={() => void saveSnapshot()}
      >
        <Plus size={15} strokeWidth={1.5} />
        {saving ? "Saving…" : "Save current version"}
      </button>

      {loading ? (
        <Spinner label="Loading history…" />
      ) : error ? (
        <ErrorState title="Couldn't load history" />
      ) : !versions || versions.length === 0 ? (
        <div data-testid="version-empty">
          <EmptyState
            icon={<Clock size={26} strokeWidth={1.5} />}
            title="No versions yet"
            body="Save a version to capture a snapshot of the current content."
          />
        </div>
      ) : (
        <ul className="ev-versions">
          {versions.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                className="ev-version"
                data-testid={`version-row-${v.id}`}
                onClick={() => void openVersion(v.id)}
              >
                <span className="ev-version__top">
                  <Avatar
                    actor={v.editor ?? actorFromLegacy(v.editor_name)}
                    size="xs"
                  />
                  <span className="ev-version__author">
                    {v.editor_name || "Unknown"}
                  </span>
                  <span className="ev-version__time">
                    {formatDate(v.created_at)}
                  </span>
                </span>
                {v.note ? <span className="ev-version__note">{v.note}</span> : null}
                <span className="ev-version__hash">
                  {v.content_sha256.slice(0, 12)}
                </span>
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm ev-version__restore"
                data-testid={`version-restore-${v.id}`}
                aria-label={`Revert to the version from ${formatDate(v.created_at)}`}
                disabled={restoring}
                onClick={() => setConfirming(v.id)}
              >
                <RotateCcw size={14} strokeWidth={1.5} />
                Revert
              </button>
            </li>
          ))}
        </ul>
      )}

      {confirming !== null ? (
        <RestoreConfirm
          versionId={confirming}
          busy={restoring}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void restore(confirming)}
        />
      ) : null}

      {report ? (
        <RestoreReportCard report={report} onDismiss={() => setReport(null)} />
      ) : null}

      {viewing ? (
        <VersionViewer
          docId={docId}
          version={viewing}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Read-only overlay showing a past version — ON THE CANVAS ENGINE.
 *
 * This used to be `dangerouslySetInnerHTML={{ __html: version.rendered_html }}`
 * with a comment arguing that a snapshot with no annotation layer made a DOM paint
 * correct. Owner decision D-E settles it the other way: no DOM renders any docz IR
 * anywhere, version history explicitly included. A reviewer comparing a revision
 * against the head must be looking at the SAME engine, or "what changed" includes
 * the renderer.
 *
 * The metadata (the timestamp in the bar) still comes from the JSON
 * `DocVersionDetail`; only the BODY moved to `<VersionCanvas>`, which fetches that
 * revision's block-DTO bytes through the negotiated binary representation.
 */
function VersionViewer({
  docId,
  version,
  onClose,
}: {
  docId: string;
  version: DocVersionDetail;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: click-scrim closes the dialog
    <div
      className="ev-viewer"
      data-testid="version-view"
      role="dialog"
      aria-modal="true"
      aria-label="Past version (read-only)"
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stops scrim close */}
      <div className="ev-viewer__panel" onClick={(e) => e.stopPropagation()}>
        <div className="ev-viewer__bar">
          <BookOpen size={16} strokeWidth={1.5} />
          <span className="ev-viewer__title">
            Version from {formatDate(version.created_at)}
          </span>
          <span className="ev-viewer__badge">read-only</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm ev-viewer__close"
            data-testid="version-view-close"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        {/* THE REVISION, painted through the same canvas engine as read mode.
            No comments and no composer: an anchor resolves against the HEAD's
            block ordinals, so bands over a historical body would land by
            coincidence. Selection and copy still work — they come from the IR. */}
        <div className="ev-viewer__body" data-testid="version-view-body">
          <VersionCanvas
            docId={docId}
            versionId={version.id}
            title={`Version from ${formatDate(version.created_at)} (read-only)`}
            testId="version-view-canvas"
          />
        </div>
      </div>
    </div>
  );
}
